import { useApolloClient } from '@apollo/client'
import { useCallback, useMemo, useState } from 'react'
import { draftQuestionnaireRefetchQuery } from '../../../data/gql/questionnaire/queries'
import { DraftQuestionnaireEntry } from '../../../data/model/questionnaire'
import {
  AmbiguousLanguageError,
  LanguageCodeError,
  toTargetLanguageCode
} from '../../../data/rest/researcherTools/languageCodes'
import ResearcherToolsTranslations from '../../../data/rest/researcherTools/translations'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'
import { MarketVisibilityFilter } from '../../Questionnaire/MarketVisibility/marketVisibility'
import {
  dispatchAudienceFieldSave,
  dispatchSurveyFieldSave,
  isPerFieldOnlyKind,
  orderPerFieldSaves
} from '../lib/dispatchFieldSave'
import {
  AudienceTranslatableField,
  CustomAudienceTranslationGroup
} from '../lib/extractAudienceFields'
import {
  TranslatableField,
  extractAllSurveyFields
} from '../lib/extractTranslatableFields'
import {
  TranslationPayloadError,
  fieldIdentity,
  fromTranslationResponse,
  toTranslationRequest
} from '../lib/translationMapping'
import { useSaveTranslation } from './useSaveTranslation'
import {
  SaveViaImportResult,
  useSaveTranslationsViaImport
} from './useSaveTranslationsViaImport'

export interface AutoTranslateProgress {
  running: boolean
  total: number
  done: number
  failed: number
  errors: string[]
}

// Module-level promise queue keyed on project ID. Different projects stay
// independent; within a project, writes are serialised to prevent concurrent
// mutations from colliding on the same questionnaire document.
const projectWriteQueues = new Map<string, Promise<void>>()

/**
 * Serialises writes to a project's questionnaire. Every translation mutation
 * rewrites the whole document under optimistic concurrency; the frontend
 * cannot detect conflicts, so we prevent them by queueing.
 */
export const enqueueProjectWrites = async <T>(
  projectId: string,
  work: () => Promise<T>
): Promise<T> => {
  const previous = projectWriteQueues.get(projectId) ?? Promise.resolve()
  let release: () => void = () => undefined
  const current = new Promise<void>((resolve) => {
    release = resolve
  })
  const queued = previous.catch(() => undefined).then(() => current)
  projectWriteQueues.set(projectId, queued)
  await previous.catch(() => undefined)
  try {
    return await work()
  } finally {
    release()
    if (projectWriteQueues.get(projectId) === queued)
      projectWriteQueues.delete(projectId)
  }
}

type SurveyJob = { field: TranslatableField; translation: string }
type AudienceJob = { field: AudienceTranslatableField; translation: string }

/**
 * Converts errors to user-facing messages. Language errors are actionable
 * (user can choose a variant or different language). Translation payload
 * errors indicate response validation failed. Everything else is a generic
 * network/service failure.
 */
const errorMessage = (error: unknown): string => {
  if (error instanceof AmbiguousLanguageError) {
    return error.message
  }
  if (error instanceof LanguageCodeError) {
    return error.message
  }
  if (error instanceof TranslationPayloadError) {
    return `The translation response could not be validated: ${error.message}`
  }
  return 'Auto-translation failed. Check your connection and retry; no questionnaire text from the failed step was saved.'
}

export const useAutoTranslate = () => {
  const save = useSaveTranslation()
  const { commit } = useSaveTranslationsViaImport()
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const apolloClient = useApolloClient()
  const translationClient = useMemo(() => new ResearcherToolsTranslations(), [])
  const [progress, setProgress] = useState<AutoTranslateProgress>({
    running: false,
    total: 0,
    done: 0,
    failed: 0,
    errors: []
  })

  const run = useCallback(
    async (
      entries: ReadonlyArray<DraftQuestionnaireEntry>,
      languageCode: string,
      audienceGroups: ReadonlyArray<CustomAudienceTranslationGroup> = [],
      isVisible?: MarketVisibilityFilter
    ) => {
      const surveyFields = extractAllSurveyFields(entries, isVisible)

      // Count all candidates: fields with non-empty master and empty translation
      const candidates = [
        ...surveyFields.filter(
          (field) =>
            field.master.trim() && !field.getCurrent(languageCode).trim()
        ),
        ...audienceGroups.flatMap((group) =>
          group.fields.filter(
            (field) =>
              field.master.trim() && !field.getCurrent(languageCode).trim()
          )
        )
      ]
      const total = candidates.length
      setProgress({ running: true, total, done: 0, failed: 0, errors: [] })

      if (!total) {
        setProgress({
          running: false,
          total: 0,
          done: 0,
          failed: 0,
          errors: []
        })
        return
      }

      // Pack, translate, unpack
      let translations: Map<string, string>
      try {
        // Resolve ambiguity/unsupported locales BEFORE any HTTP call
        toTargetLanguageCode(languageCode)
        const questions = toTranslationRequest(
          surveyFields,
          audienceGroups,
          languageCode
        )
        const response = await translationClient.translate({
          target_language_code: languageCode,
          questions
        })
        translations = fromTranslationResponse(questions, response)
      } catch (error) {
        setProgress({
          running: false,
          total,
          done: 0,
          failed: total,
          errors: [errorMessage(error)]
        })
        return
      }

      // Build jobs from translations, filtering to non-blank translations
      const surveyJobs: SurveyJob[] = surveyFields.flatMap((field) => {
        if (!field.master.trim() || field.getCurrent(languageCode).trim())
          return []
        const translation = translations.get(fieldIdentity(field))
        return translation?.trim() ? [{ field, translation }] : []
      })
      const audienceJobs: AudienceJob[] = audienceGroups.flatMap((group) =>
        group.fields.flatMap((field) => {
          if (!field.master.trim() || field.getCurrent(languageCode).trim())
            return []
          const translation = translations.get(
            fieldIdentity(field, group.memberId)
          )
          return translation?.trim() ? [{ field, translation }] : []
        })
      )
      const jobs = [...surveyJobs, ...audienceJobs]

      let done = 0
      // Fields that had no translation (no entry in the map) count as failed
      let failed = total - jobs.length
      const errors: string[] = []

      const update = () =>
        setProgress({ running: true, total, done, failed, errors: [...errors] })

      const markFailure = () => {
        failed += 1
        errors.push(
          'A questionnaire translation could not be saved. Retry to fill the remaining empty fields.'
        )
        update()
      }

      const saveSurveyJob = async (job: SurveyJob) => {
        // Guard: blank translations must never be sent (backend interprets blank
        // as delete for some field types like scale labels)
        if (!job.translation.trim()) {
          markFailure()
          return
        }
        try {
          await dispatchSurveyFieldSave(
            save,
            job.field,
            job.translation,
            languageCode
          )
          done += 1
          update()
        } catch {
          markFailure()
        }
      }

      // Serialise all writes for this project
      await enqueueProjectWrites(projectId, async () => {
        // Split survey jobs into importable (Question/Matrix) and per-field-only
        const importable = surveyJobs.filter(
          ({ field }) => !isPerFieldOnlyKind(field.kind)
        )
        const perField = surveyJobs.filter(({ field }) =>
          isPerFieldOnlyKind(field.kind)
        )

        // 1) Question/Matrix — bulk import with per-field fallback
        if (importable.length) {
          const overrides = new Map(
            importable.map(({ field, translation }) => [field.key, translation])
          )
          let result: SaveViaImportResult | undefined
          try {
            result = await commit(entries, languageCode, {
              overrides,
              isVisible
            })
          } catch {
            // Import failed atomically — fall back to serial per-field writes
            for (const job of importable) await saveSurveyJob(job)
          }
          if (result) {
            const successfulResult = result
            const residual = importable.filter(
              ({ field }) => !successfulResult.consumedKeys.has(field.key)
            )
            done += importable.length - residual.length
            update()
            // useImportQuestionnaireV2 does NOT set awaitRefetchQueries: true,
            // so the import returns before the refetch lands. Residual saves
            // must explicitly await fresh optimistic-lock state.
            try {
              if (successfulResult.importCalled) {
                await apolloClient.query({
                  ...draftQuestionnaireRefetchQuery(
                    projectId,
                    surveyId,
                    waveId
                  ),
                  fetchPolicy: 'network-only'
                })
              }
              for (const job of residual) await saveSurveyJob(job)
            } catch {
              residual.forEach(() => markFailure())
            }
          }
        }

        // 2) Per-field-only kinds (text cards, highlighters, conjoints, MaxDiff,
        // scale labels, sections). Conjoint attribute names must be written last
        // — see orderPerFieldSaves for why.
        const orderedPerField = orderPerFieldSaves(perField)
        for (const job of orderedPerField) {
          await saveSurveyJob(job)
        }

        // 3) Audience — per-field
        for (const job of audienceJobs) {
          if (!job.translation.trim()) {
            markFailure()
            continue
          }
          try {
            await dispatchAudienceFieldSave(
              save,
              job.field,
              job.translation,
              languageCode
            )
            done += 1
            update()
          } catch {
            markFailure()
          }
        }
      })

      setProgress({ running: false, total, done, failed, errors })
    },
    [apolloClient, commit, projectId, save, surveyId, translationClient, waveId]
  )

  return { run, progress }
}
